'use client';

/**
 * InstantListingAlertsCard
 *
 * Shared listing-alert basics card. Two rendering modes:
 *   1. Default (used on /saved): renders the full card — section
 *      header, notification toggle, divider, "Alert preferences"
 *      label, and the three basics fields (location, price, bedrooms),
 *      wrapped in the bordered rounded container.
 *   2. Bare / wizard mode: renders only the three basics fields
 *      directly. Used inside the Renter Profile wizard which supplies
 *      its own outer chrome and step header.
 *
 * Both modes read + write through the same `useListingAlert` hook so
 * prefill and updates round-trip against the shared BE endpoint.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Neighborhood } from '@repo/api';

import { useStore } from '@/providers/StoreProvider';
import { Divider, Popover, SearchInputPill, SectionHeader } from '@/components/ui';
import { IconAlertCircleFilled } from '@repo/svgs';
import LocationFilter from '@/features/listings/filters/location/LocationFilter';
import { useAnalytics } from '@/hooks/useAnalytics';
import { cn } from '@/lib/cn';
import { useIsMobile } from '@/hooks/useDeviceType';
import { ListingNotification } from '@/app/saved/components/ListingNotification';
import { ListingAlertPriceAndBedrooms } from '@/app/saved/components/ListingAlertPriceAndBedrooms';
import ListingAlertLocationSheet from '@/app/saved/components/ListingAlertLocationSheet';
import { useListingAlert } from '@/app/saved/hooks/useListingAlert';
import { formatListingAlertLocationLabel } from '@/app/saved/lib/listingAlertLocationLabel';

type AccordionEntry = {
  parents: { id: string }[];
  childrenByParent: Record<string, { id: string }[]>;
};

type AlertValidationErrors = {
  location: boolean;
  maxPrice: boolean;
  bedrooms: boolean;
};

const normalizeIds = (ids: string[] | null | undefined) => [...(ids ?? [])].sort();
const areIdsEqual = (left: string[] | null | undefined, right: string[] | null | undefined) =>
  JSON.stringify(normalizeIds(left)) === JSON.stringify(normalizeIds(right));

export interface InstantListingAlertsCardProps {
  /**
   * When false, hides the "New listing notifications" toggle row + the
   * "Alert preferences" label + the outer bordered container. Renders
   * only the raw form fields (location + price + bedrooms) so the
   * component can be embedded inside the Renter Profile wizard.
   * Default true (preserves /saved page appearance).
   */
  showNotificationToggle?: boolean;
  /**
   * External override to force validation-error display (used by the
   * wizard when the user taps Next with missing required fields).
   * Default undefined (component manages internal state as today).
   */
  showValidationErrors?: { location: boolean; maxPrice: boolean; bedrooms: boolean };
  /**
   * Reports whether all three basics are populated. Wizard uses this
   * to gate the Next button. Default undefined.
   */
  onValidChange?: (allBasicsSet: boolean) => void;
  /**
   * When true, skips the outer bordered container / rounded card
   * styling; component renders as a plain form. Used inside the
   * wizard modal which supplies its own outer chrome.
   * Default false.
   */
  bare?: boolean;
  /**
   * When true, forces the max-price + bedrooms row to stack
   * vertically (each on its own line) and shows required-field
   * markers ("*") on the labels. Used inside the wizard modal
   * which is narrow even on desktop and follows the Figma spec of
   * one-field-per-row. Default false — /saved keeps the compact
   * inline layout it uses today.
   */
  stackedBasics?: boolean;
  /**
   * Wizard-only field-tap callbacks. Fired when the user interacts
   * with each Basics field for the first time in a session. The
   * wizard wires these to its `screen`-scoped analytics events
   * (`preferences_location` / `preferences_max_price` /
   * `preferences_bedrooms`). Left undefined on /saved — nothing to
   * report from that surface. Explicit `| undefined` so the
   * repo's `exactOptionalPropertyTypes` allows callers to pass a
   * literal `undefined` (which forwarding wrappers commonly do).
   */
  onLocationTap?: (() => void) | undefined;
  onMaxPriceTap?: (() => void) | undefined;
  onBedroomsTap?: (() => void) | undefined;
}

export function InstantListingAlertsCard({
  showNotificationToggle = true,
  showValidationErrors,
  onValidChange,
  bare = false,
  stackedBasics = false,
  onLocationTap,
  onMaxPriceTap,
  onBedroomsTap,
}: InstantListingAlertsCardProps = {}) {
  const {
    data,
    loading,
    saving,
    setListingAlert,
    setListingAlertNeighborhoodIds,
    updateListingAlertFields,
  } = useListingAlert();
  const analytics = useAnalytics();
  const isMobile = useIsMobile();
  const [locationQuery, setLocationQuery] = useState('');
  const [locationOpen, setLocationOpen] = useState(false);
  const [draftNeighborhoodIds, setDraftNeighborhoodIds] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<AlertValidationErrors>({
    location: false,
    maxPrice: false,
    bedrooms: false,
  });
  const locationAnchorRef = useRef<HTMLDivElement | null>(null);
  const hasTrackedNeighborhoodSearchRef = useRef(false);
  const skipNextCloseCommitRef = useRef(false);
  const fetchNeighborhoods = useStore(s => s.neighborhoods.fetchNeighborhoods);
  const neighborhoods = useStore(s => s.neighborhoods.neighborhoods) as (Neighborhood & {
    childrenIds?: string[];
  })[];
  const accordion = useStore(s => s.neighborhoods.neighborhoodAccordionData) as
    | Record<string, AccordionEntry>
    | undefined;

  useEffect(() => {
    void fetchNeighborhoods();
  }, [fetchNeighborhoods]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLocationQuery('');
    }, 300);
    return () => clearTimeout(timeout);
  }, [data?.listingAlertNeighborhoodIds]);

  const committedNeighborhoodIds = data?.listingAlertNeighborhoodIds ?? [];
  const hasLocation = committedNeighborhoodIds.length > 0;
  const hasLocationSelection =
    (locationOpen ? draftNeighborhoodIds : committedNeighborhoodIds).length > 0;
  const hasMaxPrice = data?.listingAlertMaxRent !== null;
  const hasBedrooms = (data?.listingAlertNumBedrooms?.length ?? 0) > 0;
  const hasAllRequiredFields = hasLocation && hasMaxPrice && hasBedrooms;
  const selectionLabel = useMemo(() => {
    return formatListingAlertLocationLabel(committedNeighborhoodIds, neighborhoods, accordion);
  }, [committedNeighborhoodIds, neighborhoods, accordion]);

  // Report validation status upward when host component wants to gate
  // navigation (e.g. wizard's Next button).
  useEffect(() => {
    if (onValidChange) onValidChange(hasAllRequiredFields);
  }, [onValidChange, hasAllRequiredFields]);

  const toggleNeighborhood = useCallback((neighborhoodId: string) => {
    setDraftNeighborhoodIds(current => {
      const exists = current.includes(neighborhoodId);
      return exists ? current.filter(id => id !== neighborhoodId) : [...current, neighborhoodId];
    });
  }, []);

  const toggleNeighborhoodWithChildren = useCallback(
    (neighborhoodId: string) => {
      setDraftNeighborhoodIds(current => {
        if (!accordion) {
          const exists = current.includes(neighborhoodId);
          return exists
            ? current.filter(id => id !== neighborhoodId)
            : [...current, neighborhoodId];
        }

        let childrenIdsForParent: string[] | null = null;
        for (const entry of Object.values(accordion)) {
          if (entry.parents.some(parent => parent.id === neighborhoodId)) {
            childrenIdsForParent = (entry.childrenByParent[neighborhoodId] ?? []).map(
              child => child.id,
            );
            break;
          }
        }

        if (!childrenIdsForParent) {
          const exists = current.includes(neighborhoodId);
          return exists
            ? current.filter(id => id !== neighborhoodId)
            : [...current, neighborhoodId];
        }

        const selected = new Set(current);
        if (selected.has(neighborhoodId)) {
          selected.delete(neighborhoodId);
          childrenIdsForParent.forEach(id => selected.delete(id));
        } else {
          selected.add(neighborhoodId);
          childrenIdsForParent.forEach(id => selected.add(id));
        }
        return Array.from(selected);
      });
    },
    [accordion],
  );

  const toggleBorough = useCallback(
    (borough: string) => {
      const entry = accordion?.[borough];
      if (!entry) return;

      const boroughIds = [
        ...entry.parents.map(parent => parent.id),
        ...Object.values(entry.childrenByParent)
          .flat()
          .map(child => child.id),
      ];

      setDraftNeighborhoodIds(current => {
        const selected = new Set(current);
        const allSelected = boroughIds.length > 0 && boroughIds.every(id => selected.has(id));
        if (allSelected) {
          boroughIds.forEach(id => selected.delete(id));
        } else {
          boroughIds.forEach(id => selected.add(id));
        }
        return Array.from(selected);
      });
    },
    [accordion],
  );

  const commitNeighborhoodSelection = useCallback(
    async (ids: string[]) => {
      if (areIdsEqual(ids, committedNeighborhoodIds)) return;
      await setListingAlertNeighborhoodIds(ids);
    },
    [committedNeighborhoodIds, setListingAlertNeighborhoodIds],
  );

  const openLocationPopover = useCallback(() => {
    if (loading || saving) return;
    if (locationOpen) return;
    analytics.track('listings_filter_neighborhood_open');
    hasTrackedNeighborhoodSearchRef.current = false;
    setDraftNeighborhoodIds([...(data?.listingAlertNeighborhoodIds ?? [])]);
    setLocationOpen(true);
  }, [analytics, data?.listingAlertNeighborhoodIds, loading, locationOpen, saving]);

  const closeLocationSelector = useCallback(() => {
    analytics.track('listings_filter_neighborhood_close');
    setLocationOpen(false);
    setLocationQuery('');
    if (skipNextCloseCommitRef.current) {
      skipNextCloseCommitRef.current = false;
      return;
    }
    void commitNeighborhoodSelection(draftNeighborhoodIds);
  }, [analytics, commitNeighborhoodSelection, draftNeighborhoodIds]);

  const handleClearLocation = useCallback(() => {
    analytics.track('listings_filter_neighborhood_reset');
    setDraftNeighborhoodIds([]);
    void updateListingAlertFields({
      listingAlertNeighborhoodIds: [],
      ...(data?.listingAlert ? { listingAlert: false } : {}),
    });
  }, [analytics, data?.listingAlert, updateListingAlertFields]);

  const handleToggleListingAlert = useCallback(
    (next: boolean) => {
      if (!next) {
        setValidationErrors({ location: false, maxPrice: false, bedrooms: false });
        analytics.track('instant_alerts_off');
        void setListingAlert(false);
        return;
      }

      if (!hasAllRequiredFields) {
        setValidationErrors({
          location: !hasLocation,
          maxPrice: !hasMaxPrice,
          bedrooms: !hasBedrooms,
        });
        return;
      }

      setValidationErrors({ location: false, maxPrice: false, bedrooms: false });
      analytics.track('instant_alerts_on', { screen: 'saved' });
      void setListingAlert(true);
    },
    [analytics, hasAllRequiredFields, hasBedrooms, hasLocation, hasMaxPrice, setListingAlert],
  );

  useEffect(() => {
    if (hasLocationSelection && validationErrors.location) {
      setValidationErrors(current => ({ ...current, location: false }));
    }
  }, [hasLocationSelection, validationErrors.location]);

  useEffect(() => {
    if (hasMaxPrice && validationErrors.maxPrice) {
      setValidationErrors(current => ({ ...current, maxPrice: false }));
    }
  }, [hasMaxPrice, validationErrors.maxPrice]);

  useEffect(() => {
    if (hasBedrooms && validationErrors.bedrooms) {
      setValidationErrors(current => ({ ...current, bedrooms: false }));
    }
  }, [hasBedrooms, validationErrors.bedrooms]);

  // Merge internal validation state with any external override the
  // host provides. External values are OR'd on top so a host tap of
  // "Next" can force the error rows without racing internal state.
  const effectiveValidationErrors: AlertValidationErrors = {
    location: validationErrors.location || (showValidationErrors?.location ?? false),
    maxPrice: validationErrors.maxPrice || (showValidationErrors?.maxPrice ?? false),
    bedrooms: validationErrors.bedrooms || (showValidationErrors?.bedrooms ?? false),
  };

  const showOuterBorder = !bare && showNotificationToggle;
  const showToggleSection = showNotificationToggle;

  // Field-group inner padding: the /saved card indents its rows by
  // `px-2` (8px) inside its own bordered container. In `bare` mode
  // (wizard) the parent modal already supplies horizontal padding,
  // so we drop the extra inset — otherwise fields sit 8px further
  // in than labels and everything looks misaligned.
  const groupPadding = bare ? '' : 'px-2';

  const fields = (
    <div className="space-y-1">
      {/* Legacy "Alert preferences" label removed — the section is
          now titled "Basics" (rendered by SectionHeader above), and
          the alerts toggle sits at the bottom as its own card
          rather than at the top, so the intermediary label is no
          longer needed. */}
      <div
        ref={locationAnchorRef}
        className={groupPadding}
        // Fire only when the tap lands on the actual input pill or
        // one of its interactive children — clicking the "Location"
        // label above (a static div) or empty padding should NOT
        // count as user engagement with the field.
        onClick={e => {
          if (
            onLocationTap &&
            (e.target as HTMLElement).closest('input, [role="combobox"], button')
          ) {
            onLocationTap();
          }
        }}
      >
        <div className="text-body-2-high text-secondary pb-1">
          Location{stackedBasics ? ' *' : ''}
        </div>
        <SearchInputPill
          className={cn(
            'flex-1 min-w-0 !shadow-none',
            effectiveValidationErrors.location && !hasLocationSelection && '!border-alert',
          )}
          placeholder="Search neighborhoods"
          showClearButton={!!selectionLabel && !locationOpen}
          onValueChange={v => {
            setLocationQuery(v);
            if (!hasTrackedNeighborhoodSearchRef.current && v.trim().length > 0) {
              hasTrackedNeighborhoodSearchRef.current = true;
              analytics.track('listings_filter_neighborhood_search');
            }
          }}
          value={locationOpen ? locationQuery : (selectionLabel ?? '')}
          isLoading={loading}
          disabled={loading || saving}
          aria-invalid={effectiveValidationErrors.location && !hasLocationSelection}
          onClick={openLocationPopover}
          onFocus={openLocationPopover}
          {...(selectionLabel ? { onClear: handleClearLocation } : undefined)}
        />
        {effectiveValidationErrors.location && !hasLocationSelection ? (
          <div className="mt-2 flex items-center gap-1">
            <IconAlertCircleFilled className="h-4 w-4 text-alert shrink-0" />
            <span className="text-caption text-alert">Please select neighborhoods.</span>
          </div>
        ) : null}
      </div>
      <div className={groupPadding}>
        <ListingAlertPriceAndBedrooms
          maxRent={data?.listingAlertMaxRent ?? null}
          numBedrooms={data?.listingAlertNumBedrooms ?? null}
          isLoading={loading}
          isSaving={saving}
          stacked={stackedBasics}
          onMaxRentChange={value => {
            void updateListingAlertFields({
              listingAlertMaxRent: value,
              ...(value === null && data?.listingAlert ? { listingAlert: false } : {}),
            });
          }}
          onNumBedroomsChange={value => {
            void updateListingAlertFields({
              listingAlertNumBedrooms: value,
              ...(value === null && data?.listingAlert ? { listingAlert: false } : {}),
            });
          }}
          showMaxPriceError={effectiveValidationErrors.maxPrice && !hasMaxPrice}
          showBedroomsError={effectiveValidationErrors.bedrooms && !hasBedrooms}
          onMaxPriceTap={onMaxPriceTap}
          onBedroomsTap={onBedroomsTap}
        />
      </div>
    </div>
  );

  // Instant-alerts toggle is now a `bg-secondary` card sitting AFTER
  // the fields (Figma "Basics" section). Copy differs from the
  // legacy standalone alerts card — "Instant listing alerts" + the
  // static "Based on location, price, and bedrooms" subtitle.
  const alertsToggleCard = showToggleSection ? (
    <div className="w-full bg-secondary rounded-2xl px-2 py-1">
      <ListingNotification
        className="!py-3 !px-2"
        title="Instant listing alerts"
        subtitle="Based on location, price, and bedrooms"
        isLoading={loading}
        isSaving={saving}
        on={data?.listingAlert ?? false}
        onChange={handleToggleListingAlert}
      />
    </div>
  ) : null;

  const body = (
    <div className="flex flex-col gap-4">
      {/* Fields come first per Figma "Basics" section — location →
          max price → bedrooms — then the alerts toggle card. Legacy
          order (toggle at top, divider, then fields) is gone; the
          alerts row is just one preference sitting alongside the
          basics. */}
      {fields}
      {alertsToggleCard}
    </div>
  );

  return (
    <div className="w-full">
      {/* Section title = "Basics" per Figma. Only rendered when the
          toggle section is visible (i.e. on /saved). The wizard's
          `showNotificationToggle={false}` mode suppresses this
          header entirely — the wizard supplies its own section
          copy. */}
      {showToggleSection && <SectionHeader titleContainerClassName="!px-0" title="Basics" />}
      {showOuterBorder ? (
        <div className="border border-secondary md:px-4 px-2 md:pt-3 pt-1 md:pb-6 pb-4 rounded-2xl">
          {body}
        </div>
      ) : (
        body
      )}
      {isMobile ? (
        <ListingAlertLocationSheet
          open={locationOpen}
          onClose={closeLocationSelector}
          searchQuery={locationQuery}
          onSearchQueryChange={setLocationQuery}
          selectedNeighborhoodIds={draftNeighborhoodIds}
          onToggleNeighborhood={toggleNeighborhood}
          onToggleNeighborhoodWithChildren={toggleNeighborhoodWithChildren}
          onToggleBorough={toggleBorough}
          onReset={() => setDraftNeighborhoodIds([])}
          onApply={async ids => {
            skipNextCloseCommitRef.current = true;
            setDraftNeighborhoodIds(ids);
            await commitNeighborhoodSelection(ids);
            setLocationOpen(false);
            setLocationQuery('');
          }}
        />
      ) : (
        <Popover
          open={locationOpen}
          onOpenChange={open => {
            if (open) {
              setLocationOpen(true);
              return;
            }
            closeLocationSelector();
          }}
          anchorRef={locationAnchorRef as React.RefObject<HTMLElement>}
          matchAnchorWidth={false}
          className="w-120"
        >
          <LocationFilter
            requireConfirmation
            anchorRef={locationAnchorRef as React.RefObject<HTMLElement>}
            searchQuery={locationQuery}
            onSearchQueryChange={setLocationQuery}
            shouldUpdateUrl={false}
            primaryButtonText="Done"
            showSuggestedLocations={false}
            showBuildingSuggestions={false}
            selectionMode="external"
            selectedNeighborhoodIds={draftNeighborhoodIds}
            onToggleNeighborhood={toggleNeighborhood}
            onToggleNeighborhoodWithChildren={toggleNeighborhoodWithChildren}
            onToggleBorough={toggleBorough}
            onResetExternalSelection={() => setDraftNeighborhoodIds([])}
            onApplyExternalSelection={async ids => {
              skipNextCloseCommitRef.current = true;
              setDraftNeighborhoodIds(ids);
              await commitNeighborhoodSelection(ids);
              setLocationOpen(false);
              setLocationQuery('');
            }}
          />
        </Popover>
      )}
    </div>
  );
}
